import {html, LitElement, nothing} from "lit";
import {customElement, property, queryAssignedElements, state} from "lit/decorators.js";
import {dropdownStyles} from "./Dropdown.styles.js";
import {classMap} from "lit/directives/class-map.js";

import "./IconButton.js";

@customElement("pn-dropdown")
export class Dropdown extends LitElement {
	@property() icon = "three-dots";
	@property() id:                 string;
	@property() isAutoWidth = false; // automatically determine dropdown width
	@property() isDropdown = false;
	@property() isRightAligned = false; // affects styles
	@property() onToggleTrackEvent: () => void;
	@property() tooltipOptions = {
		position: String,
		text:     String
	};

	@state() isOpen = false;
	@state() isTouched = false;

	@queryAssignedElements() _dropdownMenuElems: HTMLElement[];

	static styles = [dropdownStyles];

	connectedCallback() {
		super.connectedCallback();
		document.addEventListener("click", this.onClickDocument);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener("click", this.onClickDocument);
	}

	// Close the menu when you click outside of it, if it's open
	onClickDocument = (event: MouseEvent) => {
		// Is the click target part of the dropdown?
		const test = !!event.composedPath().find(a => a === this);

		if (this.isOpen && !test)
			this.toggleDropdownMenu();
	};

	// Show/hide the dropdown. Tracks the event if one is provided.
	toggleDropdownMenu = () => {
		this.isOpen = !this.isOpen;

		if (this.isOpen && this.onToggleTrackEvent)
			this.onToggleTrackEvent();
		else {
			const iconButtonElem = this.renderRoot.querySelector("pn-icon-button");
			(iconButtonElem?.shadowRoot?.firstElementChild as HTMLButtonElement)?.focus();
		}
	};

	/**
	 * The toggle (three dots icon button) was clicked. If it's the first time,
	 * render the menu and then show it. Otherwise, show the menu.
	 */
	onClickThreeDots = () => {
		// If it's the first time clicking, render the menu by setting isTouched
		if (!this.isTouched)
			this.isTouched = true;

		this.toggleDropdownMenu();
	};

	/**
	 * Handles arrow up and arrow down keydown events on the dropdown toggle
	 * button (three dots icon button).
	 * Arrow up opens the menu and focuses the last item in it.
	 * Arrow down opens the menu and focuses the first item in it.
	 */
	onKeyDownDropdownButton = (event: KeyboardEvent) => {
		const target = event.target as HTMLElement;
		if (!target)
			return;

		const key = event.key;
		if (key === "ArrowUp" || key === "ArrowDown") {
			event.preventDefault();
			event.stopPropagation();

			if (key === "ArrowUp") {
				const openAndFocusFirstItem = async () => {
					this.toggleDropdownMenu();
					const items = this._dropdownMenuElems;
					const lastItem = items[items.length - 1];
					const lastItemFocusable = lastItem?.shadowRoot?.querySelector(".dropdown-item");

					(lastItemFocusable as HTMLElement)?.focus();
				};

				if (!this.isTouched) {
					this.isTouched = true;
					openAndFocusFirstItem();
				} else
					openAndFocusFirstItem();
			} else if (key === "ArrowDown") {
				const openAndFocusLastItem = async () => {
					this.toggleDropdownMenu();
					const firstItem = this._dropdownMenuElems[0];
					const firstItemFocusable = firstItem?.shadowRoot?.querySelector(".dropdown-item");

					(firstItemFocusable as HTMLElement)?.focus();
				};

				if (!this.isTouched) {
					this.isTouched = true;
					openAndFocusLastItem();
				} else
					openAndFocusLastItem();
			}
		}
	};

	renderHelper = () => {
		if (!this.isTouched)
			return nothing;
		else {
			return html`
				<div
					@dropdownRemoveFocus=${(event: CustomEvent) => event.stopPropagation()}
					class=${classMap({
						"dropdown-menu":           true,
						"custom-scroll-bar-light": true,
						"auto-width":              this.isAutoWidth
					})}
					role="menu"
					part="menu"
				>
					<slot></slot>
				</div>
			`;
		}
	};

	getToggleButtonId = () => {
		return `dropdown-toggle__${this.id}`;
	};

	render() {
		return html`
			<div
				class="${classMap({
					"dropup js-btn-dropup":     !this.isDropdown,
					"dropdown js-btn-dropdown": this.isDropdown,
					"dropdown-right-aligned":   this.isRightAligned,
					"open":                     this.isOpen
				})}"
			>
				<pn-icon-button
					aria-haspopup="menu"
					icon=${this.icon}
					class="${classMap({
						"btn-white":       true,
						"dropdown-toggle": true,
						"btn-dropup":      !this.isDropdown,
						"btn-dropdown":    this.isDropdown
					})}"
					id=${this.getToggleButtonId()}
					.onClick=${this.onClickThreeDots}
					.onKeyDown=${this.onKeyDownDropdownButton}
					.tooltipPosition=${this.tooltipOptions.position}
					.tooltipText=${this.tooltipOptions.text}
				></pn-icon-button>
				${this.renderHelper()}
			</div>
		`;
	}
};

declare global {
	interface HTMLElementTagNameMap {
		"pn-dropdown": Dropdown;
	}
}
